import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, Save, Package } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function AddOnsPage() {
  const { token } = useAuth();
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddOn, setEditingAddOn] = useState(null);
  const [formData, setFormData] = useState({
    name_en: "",
    name_de: "",
    description_en: "",
    description_de: "",
    info_video_url: "",
    price: 0,
    eligible_plans: ["A", "B", "C", "D"],
    icon: "star",
    shopify_variant_id: "",
    status: "active"
  });

  useEffect(() => {
    fetchAddOns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchAddOns = async () => {
    try {
      const res = await axios.get(`${API}/addons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddOns(res.data);
    } catch (error) {
      toast.error("Failed to load add-ons");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAddOn) {
        await axios.put(`${API}/addons/${editingAddOn.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/addons`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Created");
      }
      setDialogOpen(false);
      setEditingAddOn(null);
      resetForm();
      fetchAddOns();
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const handleEdit = (addon) => {
    setEditingAddOn(addon);
    setFormData({
      name_en: addon.name_en,
      name_de: addon.name_de,
      description_en: addon.description_en,
      description_de: addon.description_de,
      info_video_url: addon.info_video_url || "",
      price: addon.price,
      eligible_plans: addon.eligible_plans,
      icon: addon.icon,
      shopify_variant_id: addon.shopify_variant_id,
      status: addon.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this add-on?")) return;
    try {
      await axios.delete(`${API}/addons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted");
      fetchAddOns();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({
      name_en: "",
      name_de: "",
      description_en: "",
      description_de: "",
      info_video_url: "",
      price: 0,
      eligible_plans: ["A", "B", "C", "D"],
      icon: "star",
      shopify_variant_id: "",
      status: "active"
    });
  };

  const togglePlan = (plan) => {
    setFormData(prev => ({
      ...prev,
      eligible_plans: prev.eligible_plans.includes(plan)
        ? prev.eligible_plans.filter(p => p !== plan)
        : [...prev.eligible_plans, plan]
    }));
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-1" data-testid="addons-title">Add-Ons</h1>
          <p className="text-white/50">Optional features customers can add to any plan</p>
        </div>
        <button
          onClick={() => { setEditingAddOn(null); resetForm(); setDialogOpen(true); }}
          className="btn-accent flex items-center gap-2"
          data-testid="create-addon-btn"
        >
          <Plus className="w-5 h-5" />
          New Add-On
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      ) : addOns.length === 0 ? (
        <div className="glass-card-static p-12 text-center">
          <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h3 className="font-display text-xl text-white mb-2">No add-ons yet</h3>
          <p className="text-white/40 mb-6">Create optional features for proposals</p>
          <button
            onClick={() => setDialogOpen(true)}
            className="btn-accent inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Add-On
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {addOns.map((addon, index) => (
            <motion.div
              key={addon.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="glass-card p-6"
              data-testid={`addon-card-${addon.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-white font-medium text-lg">{addon.name_en}</h3>
                  <p className="text-white/40 text-sm">{addon.name_de}</p>
                </div>
                <span className={`px-2.5 py-1 text-xs rounded-full ${
                  addon.status === "active" 
                    ? "bg-emerald-500/15 text-emerald-400"
                    : "bg-white/5 text-white/40"
                }`}>
                  {addon.status}
                </span>
              </div>
              
              <p className="text-white/50 text-sm mb-4 line-clamp-2">{addon.description_en}</p>
              
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xl text-[#C9A96E]">{"\u20AC"}{addon.price}</span>
                <div className="flex gap-1">
                  {["A", "B", "C", "D"].map(plan => (
                    <span 
                      key={plan} 
                      className={`w-6 h-6 rounded-full text-xs flex items-center justify-center ${
                        addon.eligible_plans.includes(plan)
                          ? "bg-[#C9A96E]/20 text-[#C9A96E]"
                          : "bg-white/5 text-white/20"
                      }`}
                    >
                      {plan}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(addon)}
                  className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(addon.id)}
                  className="px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0A0A]/95 backdrop-blur-xl border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">
              {editingAddOn ? "Edit Add-On" : "New Add-On"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group mb-0">
                <label className="form-label">Name (EN) *</label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Name (DE)</label>
                <input
                  type="text"
                  value={formData.name_de}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_de: e.target.value }))}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group mb-0">
              <label className="form-label">Description (EN)</label>
              <textarea
                value={formData.description_en}
                onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                className="form-input min-h-[80px]"
                placeholder="Shown as info tooltip on public page..."
              />
            </div>

            <div className="form-group mb-0">
              <label className="form-label">Info Video URL</label>
              <input
                type="url"
                value={formData.info_video_url}
                onChange={(e) => setFormData(prev => ({ ...prev, info_video_url: e.target.value }))}
                className="form-input"
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group mb-0">
                <label className="form-label">Price ({"\u20AC"}) *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="form-input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-group mb-0">
              <label className="form-label">Available for Plans</label>
              <div className="flex gap-2">
                {["A", "B", "C", "D"].map(plan => (
                  <button
                    key={plan}
                    type="button"
                    onClick={() => togglePlan(plan)}
                    className={`w-12 h-12 rounded-xl font-semibold transition-colors ${
                      formData.eligible_plans.includes(plan)
                        ? "bg-[#C9A96E] text-white"
                        : "bg-white/5 text-white/40 hover:bg-white/10"
                    }`}
                  >
                    {plan}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setDialogOpen(false)} className="flex-1 btn-secondary">
                Cancel
              </button>
              <button type="submit" className="flex-1 btn-accent flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                {editingAddOn ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
